import { useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { LoadingSpinner } from "../../components/LoadingSpinner";
import { useConfiguration } from "../../contexts/ConfigurationProvider";
import { useWBEAuth } from "../../contexts/wbe-auth-context";
import { OFFER_PREVIEW_TOKEN_PARAM } from "../../private-pages";
import { setAuthReturnTo } from "./return-to";

/** Drops only the preview token from the fragment; any other hash (`#details`) is kept byte for byte. */
const buildReturnUrl = () => {
  const { pathname, search, hash } = window.location;
  if (!hash.includes(`${OFFER_PREVIEW_TOKEN_PARAM}=`)) return `${pathname}${search}${hash}`;
  const rest = hash
    .slice(1)
    .split("&")
    .filter((part) => !part.startsWith(`${OFFER_PREVIEW_TOKEN_PARAM}=`))
    .join("&");
  return `${pathname}${search}${rest ? `#${rest}` : ""}`;
};

/** Freezes the return URL at mount: the guard re-renders after the redirect has already moved the window to /login. */
const RedirectToLogin = ({ loginPath }: { loginPath: string }) => {
  const [returnTo] = useState(buildReturnUrl);
  const navigate = useNavigate();

  useEffect(() => {
    setAuthReturnTo(returnTo);
    navigate(loginPath, { replace: true });
  }, [returnTo, loginPath, navigate]);

  return null;
};

export const RequireWBEAuth = () => {
  useLocation();
  const { identifier } = useParams();
  const { configuration, isLoading: isConfigLoading } = useConfiguration();
  const { isAuthenticated, isLoading: isAuthLoading } = useWBEAuth();

  if (isConfigLoading || isAuthLoading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (configuration?.requireLogin && !isAuthenticated) {
    return <RedirectToLogin loginPath={`/${identifier ?? ""}/login`} />;
  }

  return <Outlet />;
};
